import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { db, functions } from "../firebase";
import { styles } from "../styles";
import { createCustomer, createLocation } from "../models/models";
import { hasCapability, isAdminTier } from "../utils/capabilities";
import HighlightText from "../components/HighlightText";
import BackButton from "../components/BackButton";

/**
 * Facility "My Buildings" — a flat list of the facility's buildings (the
 * account/customer layer is hidden; a facility IS the customer). Already
 * filtered to what this user may see. Clicking a building opens the shared
 * building detail (equipment + service history). An executive can also link
 * to a contractor's building/customer by share code.
 */
export default function FacilityBuildingsPage({
  customers = [],
  role,
  capabilities,
  actions,
  orgId,
  onOpenBuilding,
  onBack,
}) {
  const canAddBuildings = hasCapability(role, capabilities, "addLocations");

  const [search, setSearch] = useState("");
  const [linkCode, setLinkCode] = useState("");
  const [linkMsg, setLinkMsg] = useState("");
  const [linking, setLinking] = useState(false);

  const [bName, setBName] = useState("");
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // The facility's own account (container for its buildings). Created lazily
  // on the first add — a facility IS its own customer/account.
  const ensureAccountId = async () => {
    const existing = customers[0];
    if (existing) return existing.id;
    let name = "My Facility";
    try {
      const snap = await getDoc(doc(db, `organizations/${orgId}`));
      if (snap.exists() && snap.data().name) name = snap.data().name;
    } catch {
      /* fall back to default name */
    }
    const account = { ...createCustomer(name), type: "facility", facilityOrgId: orgId };
    await actions.addCustomer(account);
    return account.id;
  };

  const addBuilding = async () => {
    if (!bName.trim()) return;
    setAdding(true);
    const accountId = await ensureAccountId();
    await actions.addLocation(accountId, createLocation(bName.trim()));
    setBName("");
    setAdding(false);
  };

  // After acceptBuildingInvite, the function may report same-name
  // placeholder docs the facility already created — surface them with
  // a one-tap "remove duplicate" UI so the buildings list isn't
  // cluttered with empty doubles.
  const [conflicts, setConflicts] = useState({ buildings: [], customers: [] });
  const [removingId, setRemovingId] = useState(null);

  const linkBuilding = async () => {
    if (!linkCode.trim()) return;
    setLinking(true);
    setLinkMsg("");
    setConflicts({ buildings: [], customers: [] });
    try {
      const fn = httpsCallable(functions, "acceptBuildingInvite");
      const res = await fn({ code: linkCode.trim() });
      setLinkMsg(res.data?.message || "Linked.");
      setLinkCode("");
      setConflicts({
        buildings: res.data?.buildingConflicts || [],
        customers: res.data?.customerConflicts || [],
      });
    } catch (err) {
      setLinkMsg(`Couldn't link: ${err.message}`);
    } finally {
      setLinking(false);
    }
  };

  const removeDuplicateBuilding = async (id) => {
    const conflict = conflicts.buildings.find((c) => c.id === id);
    const ok = window.confirm(
      conflict?.hasOwnData
        ? `Delete the placeholder building "${conflict.name}"?\n\nThis building has equipment or address info on it — those will be lost. The contractor's linked version stays.`
        : `Delete the placeholder building "${conflict?.name || ""}"? The contractor's linked version stays.`
    );
    if (!ok) return;
    setRemovingId(id);
    try {
      await actions.deleteLocation(id);
      setConflicts((prev) => ({
        ...prev,
        buildings: prev.buildings.filter((c) => c.id !== id),
      }));
    } catch (err) {
      window.alert(`Couldn't delete: ${err.message}`);
    } finally {
      setRemovingId(null);
    }
  };

  const removeDuplicateCustomer = async (id) => {
    const conflict = conflicts.customers.find((c) => c.id === id);
    const ok = window.confirm(
      conflict?.hasOwnData
        ? `Delete the placeholder customer "${conflict.name}"?\n\nIt has buildings under it — those go away too. The contractor's linked version stays.`
        : `Delete the placeholder customer "${conflict?.name || ""}"? The contractor's linked version stays.`
    );
    if (!ok) return;
    setRemovingId(id);
    try {
      await actions.deleteCustomer(id);
      setConflicts((prev) => ({
        ...prev,
        customers: prev.customers.filter((c) => c.id !== id),
      }));
    } catch (err) {
      window.alert(`Couldn't delete: ${err.message}`);
    } finally {
      setRemovingId(null);
    }
  };

  // Flatten buildings across the facility's account(s), remembering each
  // building's parent account so the detail page (which needs the account)
  // works on click.
  const buildings = customers.flatMap((c) =>
    (c.locations || []).map((b) => ({ building: b, account: c }))
  );

  const filtered = buildings
    .filter(({ building }) =>
      (building.name || "").toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => a.building.name.localeCompare(b.building.name));

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>My Buildings</h2>

      {canAddBuildings && (
        <>
          <h3
            onClick={() => setShowAdd((s) => !s)}
            style={{ cursor: "pointer" }}
          >
            {showAdd ? "▼" : "▶"} Add Building
          </h3>
          {showAdd && (
            <>
              <input
                style={styles.input}
                placeholder="Building Name"
                value={bName}
                onChange={(e) => setBName(e.target.value)}
              />
              <button
                style={styles.button}
                onClick={addBuilding}
                disabled={adding || !bName.trim()}
              >
                {adding ? "Adding..." : "Add Building"}
              </button>
            </>
          )}
        </>
      )}

      <h3
        onClick={() => setShowSearch((s) => !s)}
        style={{ cursor: "pointer" }}
      >
        {showSearch ? "▼" : "▶"} Search
      </h3>
      {showSearch && (
        <>
          <input
            style={styles.input}
            placeholder="Search buildings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button style={styles.button} onClick={() => setSearch("")}>
            Clear
          </button>
        </>
      )}

      {filtered.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>
          No buildings to show.
        </p>
      ) : (
        <ul>
          {filtered.map(({ building, account }) => (
            <li key={building.id} style={styles.card}>
              <button
                style={styles.button}
                onClick={() => onOpenBuilding(account, building)}
              >
                <HighlightText text={building.name} search={search} /> (
                Equipment: {(building.equipment || []).length})
              </button>
              {(building.city || building.state) && (
                <span style={{ fontSize: "12px", color: "#666", marginLeft: "8px" }}>
                  {building.city || ""}
                  {building.city && building.state ? ", " : ""}
                  {building.state || ""}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {isAdminTier(role) && (
        <div style={styles.card}>
          <strong>Link a building or contractor (by share code)</strong>
          <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
            Enter a code your contractor gave you to see a building (or all
            your buildings) and its service history here.
          </p>
          <input
            style={styles.input}
            placeholder="Share code"
            value={linkCode}
            onChange={(e) => setLinkCode(e.target.value)}
          />
          <button
            style={styles.button}
            onClick={linkBuilding}
            disabled={linking || !linkCode.trim()}
          >
            {linking ? "Linking..." : "Link"}
          </button>
          {linkMsg && (
            <p style={{ fontSize: "12px", marginTop: "4px" }}>{linkMsg}</p>
          )}

          {(conflicts.buildings.length > 0 ||
            conflicts.customers.length > 0) && (
            <div
              style={{
                marginTop: "10px",
                padding: "10px",
                background: "#fff7e6",
                border: "1px solid #f0c674",
                borderRadius: "6px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#8a5a00",
                  marginBottom: "6px",
                }}
              >
                Possible duplicates
              </div>
              <div
                style={{ fontSize: "12px", color: "#5a4000", marginBottom: "8px" }}
              >
                You already have records with these names that you created
                yourself. The contractor's linked version is now visible too —
                you can remove the duplicate to keep the list clean.
              </div>
              {conflicts.customers.map((c) => (
                <div
                  key={c.id}
                  style={{ fontSize: "13px", marginBottom: "4px" }}
                >
                  Customer: <strong>{c.name}</strong>
                  {c.hasOwnData && (
                    <span style={{ color: "#a55", marginLeft: "4px" }}>
                      (has data — will be lost if removed)
                    </span>
                  )}{" "}
                  <button
                    style={{ ...styles.button, color: "red" }}
                    onClick={() => removeDuplicateCustomer(c.id)}
                    disabled={removingId === c.id}
                  >
                    {removingId === c.id ? "Removing..." : "Remove duplicate"}
                  </button>
                </div>
              ))}
              {conflicts.buildings.map((c) => (
                <div
                  key={c.id}
                  style={{ fontSize: "13px", marginBottom: "4px" }}
                >
                  Building: <strong>{c.name}</strong>
                  {c.hasOwnData && (
                    <span style={{ color: "#a55", marginLeft: "4px" }}>
                      (has data — will be lost if removed)
                    </span>
                  )}{" "}
                  <button
                    style={{ ...styles.button, color: "red" }}
                    onClick={() => removeDuplicateBuilding(c.id)}
                    disabled={removingId === c.id}
                  >
                    {removingId === c.id ? "Removing..." : "Remove duplicate"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
